import { Injectable, signal, inject } from '@angular/core'
import { Observable } from 'rxjs'
import { environment } from 'src/environments/environment'
import { HttpClient } from '@angular/common/http'
import { AuthService } from '@auth0/auth0-angular'
import { firstValueFrom } from 'rxjs'
import { ApiResponse } from 'src/app/shared/interfaces/api-response/api-response.interface'

export interface AiChatResponse {
    conversationId: number
    threadId: string
    content: string
    agentName: string
    inputTokens: number | null
    outputTokens: number
    totalTokens: number
}

export interface AiMessage {
    id: string
    content: string
    isUser: boolean
    timestamp: Date
}

export enum AiMode {
    Chat = 'chat',
    Boiler = 'boiler',
}

@Injectable({
    providedIn: 'root',
})
export class AiPromptService {
    #http = inject(HttpClient)
    #auth = inject(AuthService)

    #conversationHistory = signal<AiMessage[]>([])
    conversationHistory = this.#conversationHistory.asReadonly()

    #conversationId = signal<number | null>(null)
    conversationId = this.#conversationId.asReadonly()

    #usageTokens = signal<{ inputTokens: number; outputTokens: number; totalTokens: number }>({
        inputTokens: 0,
        outputTokens: 0,
        totalTokens: 0,
    })
    usageTokens = this.#usageTokens.asReadonly()

    #mode = signal<AiMode>(AiMode.Chat)
    mode = this.#mode.asReadonly()

    #currentAiMessageId: string | null = null
    #abortController: AbortController | null = null

    setMode(mode: AiMode) {
        if (this.#mode() !== mode) {
            this.clearHistory()
            this.#mode.set(mode)
        }
    }

    sendPrompt(prompt: string): Observable<string> {
        const endpoint = this.#mode() === AiMode.Boiler ? 'ai/agent/boiler' : 'ai/agent/crm'
        return this.#executePrompt(prompt, endpoint)
    }

    #executePrompt(prompt: string, endpoint: string): Observable<string> {
        // Add user message
        const userMessage: AiMessage = {
            id: crypto.randomUUID(),
            content: prompt,
            isUser: true,
            timestamp: new Date(),
        }

        this.#conversationHistory.update((history) => [...history, userMessage])

        // Create a new AI message placeholder
        this.#currentAiMessageId = crypto.randomUUID()
        const aiMessage: AiMessage = {
            id: this.#currentAiMessageId,
            content: '',
            isUser: false,
            timestamp: new Date(),
        }
        this.#conversationHistory.update((history) => [...history, aiMessage])

        // Use HttpClient to get the full response from the non-streaming endpoint
        return new Observable<string>((observer) => {
            const params: any = { prompt: prompt, skipGlobalLoading: 'true' }
            if (this.#conversationId()) {
                params.conversationId = this.#conversationId()
            }

            this.#http.get<ApiResponse<AiChatResponse>>(`${environment.baseUrl}${endpoint}`, { params }).subscribe({
                next: ({ result }) => {
                    // Store the conversation ID
                    this.#conversationId.set(result.conversationId)

                    // Accumulate token usage
                    this.#usageTokens.update((usage) => ({
                        inputTokens: usage.inputTokens + (result.inputTokens || 0),
                        outputTokens: usage.outputTokens + result.outputTokens,
                        totalTokens: usage.totalTokens + result.totalTokens,
                    }))

                    // Update the AI message with the full response
                    this.#conversationHistory.update((history) => {
                        return history.map((msg) => {
                            if (msg.id === this.#currentAiMessageId) {
                                return { ...msg, content: result.content }
                            }
                            return msg
                        })
                    })

                    observer.next(result.content)
                    observer.complete()
                },
                error: (error) => {
                    console.error('API Error:', error)
                    observer.error(error)
                },
            })
        })

        /* PREVIOUS STREAMING IMPLEMENTATION - COMMENTED OUT */
        // Return observable that streams the SSE response
        // return new Observable<string>((observer) => {
        //     this.#abortController = new AbortController()
        //     const encodedPrompt = encodeURIComponent(prompt)
        //     let buffer = ''

        //     // Get the Auth0 token and use it in the fetch request
        //     firstValueFrom(this.#auth.getAccessTokenSilently())
        //         .then((token) => {
        //             return fetch(
        //                 `${environment.baseUrl}${endpoint}?prompt=${encodedPrompt}&conversationId=${this.#conversationId()}`,
        //                 {
        //                     method: 'GET',
        //                     headers: {
        //                         Accept: 'text/event-stream',
        //                         Authorization: `Bearer ${token}`,
        //                     },
        //                     signal: this.#abortController!.signal,
        //                 }
        //             )
        //         })
        //         .then(async (response) => {
        //             if (!response.ok) {
        //                 throw new Error(`HTTP error! status: ${response.status}`)
        //             }

        //             const reader = response.body?.getReader()
        //             const decoder = new TextDecoder()

        //             if (!reader) {
        //                 throw new Error('Response body is null')
        //             }

        //             while (true) {
        //                 const { done, value } = await reader.read()
        //                 if (done) break

        //                 const chunk = decoder.decode(value, { stream: true })
        //                 buffer += chunk

        //                 console.log('Received chunk:', chunk)

        //                 const messages = buffer.split('\n\n')
        //                 buffer = messages.pop() || ''

        //                 for (const message of messages) {
        //                     // if (!message.trim()) continue

        //                     const lines = message.split('\n')

        //                     for (const line of lines) {
        //                         if (line.startsWith('data:')) {
        //                             let data = line.substring(6)

        //                             if (data.trim() === '[DONE]') {
        //                                 observer.complete()
        //                                 return
        //                             }

        //                             // Convert empty chunks to newlines
        //                             // The backend sends empty data for line breaks
        //                             if (data === '') {
        //                                 data = '\n'
        //                             }

        //                             // Update the AI message content incrementally
        //                             this.#conversationHistory.update((history) => {
        //                                 return history.map((msg) => {
        //                                     if (msg.id === this.#currentAiMessageId) {
        //                                         // console.log('Appending chunk:', data)
        //                                         return { ...msg, content: msg.content + data }
        //                                     }
        //                                     return msg
        //                                 })
        //                             })

        //                             observer.next(data)
        //                         } else {
        //                             this.#conversationHistory.update((history) => {
        //                                 return history.map((msg) => {
        //                                     if (msg.id === this.#currentAiMessageId) {
        //                                         // console.log('Appending chunk:', data)
        //                                         return { ...msg, content: msg.content + '\n' }
        //                                     }
        //                                     return msg
        //                                 })
        //                             })
        //                         }
        //                     }
        //                 }
        //             }

        //             // Fix markdown formatting after streaming completes
        //             // this.#fixMarkdownFormatting()
        //             observer.complete()
        //         })
        //         .catch((error) => {
        //             if (error.name === 'AbortError') {
        //                 console.log('Stream aborted')
        //             } else {
        //                 console.error('SSE Error:', error)
        //                 observer.error(error)
        //             }
        //         })

        //     // Cleanup function
        //     return () => {
        //         this.#abortController?.abort()
        //     }
        // })
    }

    clearHistory() {
        this.#conversationHistory.set([])
        this.#currentAiMessageId = null
        this.#conversationId.set(null)
        this.#usageTokens.set({ inputTokens: 0, outputTokens: 0, totalTokens: 0 })
    }

    sendBoilerPrompt(prompt: string): Observable<string> {
        return this.#executePrompt(prompt, 'ai/prompt/boiler')
    }

    #fixMarkdownFormatting() {
        // Fix markdown heading syntax by adding space after # if missing
        this.#conversationHistory.update((history) => {
            return history.map((msg) => {
                if (msg.id === this.#currentAiMessageId) {
                    let fixedContent = msg.content

                    // Fix headings: add space after # if missing
                    fixedContent = fixedContent.replace(/^(#{1,6})([^\s#])/gm, '$1 $2')

                    // Fix lists: convert bullet points to proper markdown
                    // Replace • bullets with -
                    fixedContent = fixedContent.replace(/^(\s*)•\s+/gm, '$1- ')

                    // Ensure list items have proper spacing
                    fixedContent = fixedContent.replace(/^-([^\s])/gm, '- $1')

                    // Fix table formatting: ensure pipes have spaces
                    // Fix double pipes (||) to single pipe with space (| )
                    fixedContent = fixedContent.replace(/\|\|/g, '| ')

                    // Fix separator rows: ensure dashes have spaces
                    // Match |--...--| patterns and add spaces
                    fixedContent = fixedContent.replace(/\|(-{2,})\|/g, '| $1 |')

                    // Fix cell content: ensure | is followed by space
                    fixedContent = fixedContent.replace(/\|([^\s\|])/g, '| $1')

                    // Fix cell content: ensure space before |
                    fixedContent = fixedContent.replace(/([^\s\|])\|/g, '$1 |')

                    return { ...msg, content: fixedContent }
                }
                return msg
            })
        })
    }
}
